
import React, { useEffect, useState } from 'react';
import { ResizableTable } from './ResizableTable';
import { AiCategorizationFields, CATEGORY_LABELS, URGENCY_COLORS } from './feedbackTypes';
import './BugReportsSection.scss';

// bugReportId is kept as the field name on purpose — it's the live table's
// partition key attribute name, unchanged by the bug-report -> feedback rename.
export interface FeedbackSubmission extends AiCategorizationFields {
    bugReportId: string;
    createdAt: string;
    userId: string;
    description: string;
    system: string;
    browser: string;
    version?: string;
    status?: string;
    // Added when "Report a Bug" became "Share Feedback" — older items predate these.
    feedbackType?: 'bug' | 'suggestion' | 'confusing' | 'love' | 'general';
    rating?: number;
    screen?: string;
    memoryId?: string;
    postId?: string;
    navigationPath?: string;
}

const FEEDBACK_TYPE_LABELS: Record<string, string> = {
    bug: 'Bug',
    suggestion: 'Suggestion',
    confusing: 'Confusing',
    love: 'Love',
    general: 'General',
};


export type FeedbackSubmissionsSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

export const FeedbackSubmissionsSection: React.FC<FeedbackSubmissionsSectionProps> = ({ API_BASE, authHeaders }) => {
    const [feedbackItems, setFeedbackItems] = useState<FeedbackSubmission[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<'createdAt' | 'userId' | 'status'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    // The status value to update to (from dropdown)
    const [massActionStatus, setMassActionStatus] = useState<string>('Unreviewed');
    const [massActionLoading, setMassActionLoading] = useState(false);
    const [massActionError, setMassActionError] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<FeedbackSubmission | null>(null);

    const fetchFeedback = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/feedback`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to fetch feedback.');
            }
            const data = await res.json();
            setFeedbackItems(Array.isArray(data.items) ? data.items : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving feedback.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeedback();
    }, []);

    // Selection is managed by ResizableTable

    // Accepts updateValue from dropdown
    const handleMassUpdateStatus = async (selected: FeedbackSubmission[], updateValue?: string) => {
        setMassActionLoading(true);
        setMassActionError(null);
        try {
            const statusToSet = updateValue || massActionStatus;
            const updates = selected.map(item => ({
                bugReportId: item.bugReportId,
                createdAt: item.createdAt,
                status: statusToSet
            }));
            const res = await fetch(`${API_BASE}/feedback`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updates)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to update feedback status.');
            }
            await fetchFeedback();
        } catch (err: any) {
            setMassActionError(err.message || 'Failed to update status.');
        } finally {
            setMassActionLoading(false);
        }
    };

    const handleMassDelete = async (selected: FeedbackSubmission[]) => {
        setMassActionLoading(true);
        setMassActionError(null);
        try {
            const deletes = selected.map(item => ({
                bugReportId: item.bugReportId,
                createdAt: item.createdAt
            }));
            const res = await fetch(`${API_BASE}/feedback`, {
                method: 'DELETE',
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(deletes)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to delete feedback.');
            }
            await fetchFeedback();
        } catch (err: any) {
            setMassActionError(err.message || 'Failed to delete feedback.');
        } finally {
            setMassActionLoading(false);
        }
    };

    // Define columns for ResizableTable
    const feedbackColumns = {
        bugReportId: 30,
        userId: 30,
        createdAt: 30,
        status: 120,
        feedbackType: 120,
        rating: 70,
        aiCategory: 140,
        aiUrgency: 90,
        description: 320,
        system: 120,
        version: 140,
        browser: 120,
    };

    const sortFields: Array<{ value: 'createdAt' | 'userId' | 'status', label: string }> = [
        { value: 'createdAt', label: 'Created At' },
        { value: 'userId', label: 'User ID' },
        { value: 'status', label: 'Status' }
    ];

    const sortedFeedback = [...feedbackItems].sort((a, b) => {
        const fieldA = a[sortField] ?? '';
        const fieldB = b[sortField] ?? '';
        if (typeof fieldA === 'number' && typeof fieldB === 'number') {
            return sortOrder === 'asc' ? fieldA - fieldB : fieldB - fieldA;
        }
        if (fieldA === fieldB) return 0;
        if (sortOrder === 'asc') {
            return String(fieldA) > String(fieldB) ? 1 : -1;
        } else {
            return String(fieldA) < String(fieldB) ? 1 : -1;
        }
    });

    // No dynamic columns needed for ResizableTable

    // Status options for the updates dropdown
    const statusOptions = [
        'Unreviewed',
        'Considering',
        'In-progress',
        'Implemented'
    ];

    return (
        <section className="table-section" style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <h2>Feedback Submissions</h2>
            <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                <ResizableTable
                    columns={feedbackColumns}
                    data={sortedFeedback}
                    onMassActionSubmit={() => {}}
                    onRefresh={fetchFeedback}
                    refreshButton={true}
                    sortable={true}
                    sortableColumns={['bugReportId', 'userId', 'createdAt', 'status', 'feedbackType', 'rating', 'aiCategory', 'aiUrgency', 'description', 'system', 'version', 'browser']}
                    defaultSortField="createdAt"
                    defaultSortOrder="desc"
                    deleteButton={true}
                    onDelete={selected => handleMassDelete(selected as FeedbackSubmission[])}
                    submitButton={true}
                    onSubmit={(selected, updateValue) => handleMassUpdateStatus(selected as FeedbackSubmission[], updateValue)}
                    submitLabel="Update Status"
                    updatesDropdownEnabled={true}
                    updatesDropdownLabel="Status"
                    updatesDropdownOptions={statusOptions}
                    updatesDropdownDefault={statusOptions[0]}
                    onRowClick={row => setSelectedItem(row as FeedbackSubmission)}
                />
            </div>
            {error && <span style={{ color: 'red', marginLeft: 16 }}>{error}</span>}
            {loading && <span style={{ marginLeft: 16 }}>Processing...</span>}

            {selectedItem && (
                <div className="bug-reports-modal-overlay" onClick={() => setSelectedItem(null)}>
                    <div className="bug-reports-modal" onClick={e => e.stopPropagation()}>
                        <h3>Feedback Submission</h3>
                        <div className="bug-reports-modal-field">
                            <label>Report ID</label>
                            <p>{selectedItem.bugReportId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>User ID</label>
                            <p>{selectedItem.userId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Submitted</label>
                            <p>{new Date(selectedItem.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Status</label>
                            <p>{selectedItem.status || 'Unreviewed'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Feedback Type</label>
                            <p>{selectedItem.feedbackType ? (FEEDBACK_TYPE_LABELS[selectedItem.feedbackType] || selectedItem.feedbackType) : '—'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Rating</label>
                            <p>{selectedItem.rating ? `${selectedItem.rating} / 5` : '—'}</p>
                        </div>
                        {selectedItem.aiCategory && (
                            <div className="bug-reports-modal-field">
                                <label>AI Category</label>
                                <p>{CATEGORY_LABELS[selectedItem.aiCategory] || selectedItem.aiCategory}</p>
                            </div>
                        )}
                        {selectedItem.aiUrgency && (
                            <div className="bug-reports-modal-field">
                                <label>AI Urgency</label>
                                <p style={{ color: URGENCY_COLORS[selectedItem.aiUrgency], fontWeight: 600 }}>
                                    {selectedItem.aiUrgency.toUpperCase()}
                                </p>
                            </div>
                        )}
                        {selectedItem.aiSummary && (
                            <div className="bug-reports-modal-field">
                                <label>AI Summary</label>
                                <p>{selectedItem.aiSummary}</p>
                            </div>
                        )}
                        <div className="bug-reports-modal-field">
                            <label>System</label>
                            <p>{selectedItem.system}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Version</label>
                            <p>{selectedItem.version || '—'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Browser</label>
                            <p>{selectedItem.browser || '—'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Screen</label>
                            <p>{selectedItem.screen || '—'}</p>
                        </div>
                        {selectedItem.memoryId && (
                            <div className="bug-reports-modal-field">
                                <label>Memory ID</label>
                                <p>{selectedItem.memoryId}</p>
                            </div>
                        )}
                        {selectedItem.postId && (
                            <div className="bug-reports-modal-field">
                                <label>Post ID</label>
                                <p>{selectedItem.postId}</p>
                            </div>
                        )}
                        <div className="bug-reports-modal-field">
                            <label>Description</label>
                            <p>{selectedItem.description}</p>
                        </div>
                        <button className="bug-reports-modal-close" onClick={() => setSelectedItem(null)}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};
